
import { defineComponent, watch, ref, computed, nextTick } from "vue";
import Button from "../Buttons/Button.vue";
import SvgIcon from "@jamescoyle/vue-icon";
import {
  mdiCash,
  mdiCashPlus,
  mdiCashRefund,
  mdiClose,
  mdiCreditCardOutline,
  mdiLoading,
} from "@mdi/js";
import Modal from "../General/Modal.vue";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import { useStore } from "@/stores/order";
import { useStore as useCashRegisterStore } from "@/stores/cashRegister";
import ValidationFormErrors from "../Forms/ValidationFormErrors.vue";
import RegisterInvoicePaymentModal from "./RegisterInvoicePaymentModal.vue";

export default defineComponent({
  setup() {
    const orderStore = useStore();
    const cashRegisterStore = useCashRegisterStore();

    const page = usePage();

    const paymentForm = ref(
      useForm({
        cash_register: cashRegisterStore.selected?.id,
        cash: orderStore.totalIncMva,
        card: 0,
        invoice: 0,
        cash_received: 0,
      })
    );

    watch(
      () => cashRegisterStore.selected,
      (newValue) => {
        paymentForm.value.cash_register = newValue?.id;
      }
    );

    watch(
      () => orderStore.totalIncMva,
      (newValue) => {
        paymentForm.value = useForm({
          cash_register: paymentForm.value.cash_register,
          cash: orderStore.totalIncMva,
          card: 0,
          invoice: 0,
          cash_received: 0,
        });
      }
    );

    // The pluses in front is to convert to number in case they are empty strings;
    const totalInForm = computed(
      () =>
        +paymentForm.value.cash +
        +paymentForm.value.card +
        +paymentForm.value.invoice
    );

    // If invoice is above 0, cash cannot be used, so set cash to 0
    watch(
      () => paymentForm.value.cash,
      (newValue) => {
        const totalOrder = orderStore.totalIncMva;

        if (paymentForm.value.invoice > 0) {
          if (newValue > 0) {
            paymentForm.value.invoice = 0;
            paymentForm.value.card = totalOrder - newValue;
          }
          return;
        }

        paymentForm.value.card -= totalInForm.value - totalOrder;
      }
    );

    // If invoice is above 0, card cannot be used, so set card to 0. Else we make sure cash + card makes up to the total
    watch(
      () => paymentForm.value.card,
      (newValue) => {
        const totalOrder = orderStore.totalIncMva;

        console.log({ totalOrder, newValue });

        if (paymentForm.value.invoice > 0) {
          if (newValue > 0) {
            paymentForm.value.invoice = 0;
            paymentForm.value.cash = totalOrder - newValue;
          }
          return;
        }

        paymentForm.value.cash -= totalInForm.value - totalOrder;
      }
    );

    watch(
      () => paymentForm.value.invoice,
      (newValue) => {
        if (newValue > 0) {
          paymentForm.value.card = 0;
          paymentForm.value.cash = 0;
        } else {
          paymentForm.value.cash += orderStore.totalIncMva - totalInForm.value;
        }
      }
    );

    const showPaymentModal = ref(false);

    const addCash = (amount: number) => {
      paymentForm.value.cash_received =
        +paymentForm.value.cash_received + amount;
    };

    const cashBack = ref(0);

    const showConfirmMobileModal = ref(false);

    const sendPaymentForm = (confirmed = false) => {
      console.log("Sending payment");

      console.log({
        form: paymentForm.value,
      });

      if (
        paymentForm.value.card > 0 &&
        cashRegisterStore.selected?.terminal_is_mobile &&
        !confirmed
      ) {
        showConfirmMobileModal.value = true;
        return;
      }

      showConfirmMobileModal.value = false;

      orderStore.actionsLocked = true;

      paymentForm.value.post(
        route("orders.payments.store", { order: orderStore.order_id }),
        {
          onSuccess: () => {
            const cash_back = page.props.value.cash_back;
            if (typeof cash_back === "number" && cash_back > 0) {
              cashBack.value = cash_back;
            } else {
              showPaymentModal.value = false;
            }
          },
          onError: () => {
            showPaymentModal.value = true;
            cashBack.value = 0;
          },
          onCancel: () => {
            cashBack.value = 0;
          },
          onStart: () => {
            cashBack.value = 0;
          },
          onFinish: () => {
            orderStore.actionsLocked = false;
          },
        }
      );
    };

    const payWithCard = () => {
      paymentForm.value.card = orderStore.totalIncMva;
      // Next tick, so the watch functions above runs before we send the form
      nextTick(() => {
        sendPaymentForm();
      });
    };

    const payingWithOnlyCard = computed(() => {
      return (
        paymentForm.value.processing &&
        paymentForm.value.card === orderStore.totalIncMva
      );
    });

    const buttonsDisabled = computed(() => {
      return (
        paymentForm.value.processing ||
        orderStore.loadingOrderlines.length > 0 ||
        (totalInForm.value === 0 && orderStore.orderlines.length === 0) ||
        orderStore.openElsewhere ||
        orderStore.actionsLocked
      );
    });

    const showRegisterInvoicePaymentModal = ref(false);

    const toCurrency = (price: number, round: boolean = false) => {
      const formattedPrice = price.toLocaleString("nb", {
        maximumFractionDigits: round ? 0 : 2,

      });
      return `kr ${formattedPrice}`;
    };


    const selectText = (e: FocusEvent) => {
      if (e.target instanceof HTMLInputElement) {
        e?.target?.select();
      }
    };

    watch(
      () => paymentForm.value.processing,
      (newValue) => {
        orderStore.setPaymentProcessing(newValue);
      }
    );

    return {
      addCash,
      paymentForm,
      sendPaymentForm,
      payWithCard,
      toCurrency,
      cashBack,
      page,
      selectText,
      selectedCashRegister: computed(() => cashRegisterStore.selected),
      buttonsDisabled,
      payingWithOnlyCard,
      showConfirmMobileModal,
      showRegisterInvoicePaymentModal,
      payed: computed(() => orderStore.isPayed),
      isInvoiced: computed(() => orderStore.isInvoiced),
      totalIncMva: computed(() => orderStore.totalIncMva),
      showPaymentModal,
      cardIcon: mdiCreditCardOutline,
      cashIcon: mdiCash,
      cashBackIcon: mdiCashRefund,
      closeIcon: mdiClose,
      loadingIcon: mdiLoading,
      invoicePaymentIcon: mdiCashPlus,
    };
  },
  components: {
    Button,
    SvgIcon,
    Modal,
    ValidationFormErrors,
    RegisterInvoicePaymentModal,
  },
});
